import React from 'react';
import { Document, Image, Page, StyleSheet, Text, View } from '@react-pdf/renderer';
import { CORES_FCJ, CORES_FCJ_TEXTO } from '../utils/fcjCalculo';
import CurvaResistenciaChart from './pdf/CurvaResistenciaChart';
import logoIcon from '../assets/concreto_tec_logo.png';
import { LogoRelatorio, AssinaturasRodape } from './pdf/PersonalizacaoRelatorio';

// Estilos de cabeçalho/tabela/rodapé espelham EnsaioCompressaoPDF.js (mesmas margens
// ABNT já validadas no relatório sequencial: paddingTop 22.68, paddingBottom 92,
// paddingLeft/Right 28.35). Mantidos localmente (não compartilhados) para não alterar o
// documento do relatório sequencial já em produção.
const styles = StyleSheet.create({
  page: {
    fontFamily: 'Helvetica',
    fontSize: 7,
    paddingTop: 22.68,
    paddingBottom: 92,
    paddingLeft: 28.35,
    paddingRight: 28.35,
  },
  // Capa
  coverPage: {
    fontFamily: 'Helvetica',
    padding: 48,
  },
  coverBrandRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  coverIcon: {
    width: 48,
    height: 48,
  },
  coverBrandText: {
    marginLeft: 14,
  },
  coverWordmark: {
    fontSize: 18,
    fontFamily: 'Helvetica-Bold',
  },
  coverTagline: {
    fontSize: 8,
    letterSpacing: 1,
    color: '#0e7490',
    marginTop: 2,
  },
  coverSpacer: {
    flex: 1,
    justifyContent: 'flex-end',
  },
  coverTitleBlock: {
    paddingTop: 40,
    borderTop: '1.5pt solid #000',
  },
  coverKicker: {
    fontSize: 9,
    letterSpacing: 1,
    color: '#666',
    marginBottom: 8,
  },
  coverTitle: {
    fontSize: 20,
    fontFamily: 'Helvetica-Bold',
    lineHeight: 1.25,
  },
  coverSubtitle: {
    fontSize: 10,
    color: '#666',
    marginTop: 6,
  },
  coverMetaGrid: {
    marginTop: 28,
  },
  coverMetaField: {
    marginBottom: 14,
  },
  coverMetaLabel: {
    fontSize: 7,
    letterSpacing: 0.5,
    color: '#888',
    marginBottom: 3,
  },
  coverMetaValue: {
    fontSize: 10,
    borderBottom: '0.75pt solid #ccc',
    paddingBottom: 5,
  },
  coverFooter: {
    paddingTop: 16,
    flexDirection: 'row',
    justifyContent: 'space-between',
    fontSize: 8,
    color: '#888',
  },
  headerBox: {
    border: '1pt solid #000',
    marginBottom: 4,
  },
  headerTop: {
    flexDirection: 'row',
    alignItems: 'stretch',
  },
  headerTitleBlock: {
    flex: 1,
    padding: 6,
    alignItems: 'center',
    justifyContent: 'center',
    borderBottom: '1pt solid #000',
  },
  headerTitle: {
    fontSize: 11,
    fontFamily: 'Helvetica-Bold',
    textAlign: 'center',
  },
  headerSubtitle: {
    fontSize: 9,
    textAlign: 'center',
    marginTop: 2,
  },
  headerBrandBlock: {
    width: 116,
    padding: '4 8',
    justifyContent: 'center',
    gap: 3,
    borderLeft: '1pt solid #000',
  },
  logoPlaceholder: {
    width: 100,
    height: 60,
    border: '0.75pt dashed #999',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 3,
  },
  headerEmpresaLabel: {
    fontSize: 6,
    fontFamily: 'Helvetica-Bold',
    textTransform: 'uppercase',
  },
  headerEmpresaValue: {
    fontSize: 6.5,
    lineHeight: 1.2,
  },
  // Largura = headerBrandBlock.width, para que headerIdBar (flex: 1) termine exatamente
  // onde headerBrandBlock termina, alinhado à linha acima.
  headerBrandFill: {
    width: 116,
    borderLeft: '1pt solid #000',
  },
  headerLower: {
    flexDirection: 'row',
  },
  // Coluna do QR, à direita de headerLowerLeft, alinhada com a largura de
  // headerBrandBlock (logo) na linha de cima. Estica (stretch padrão) para cobrir a
  // altura combinada da faixa preta + as 3 linhas de metadados; ancorada no topo para
  // começar logo abaixo do "Endereço".
  headerQrColumn: {
    width: 116,
    borderLeft: '1pt solid #000',
    backgroundColor: '#fafafa',
    alignItems: 'center',
    justifyContent: 'flex-start',
    padding: '2 6',
    gap: 2,
  },
  metaQrPlaceholder: {
    width: 54,
    height: 54,
    border: '0.75pt dashed #999',
    alignItems: 'center',
    justifyContent: 'center',
  },
  metaQrCaption: {
    fontSize: 4.5,
    lineHeight: 1.1,
    color: '#444',
    textAlign: 'center',
  },
  headerLowerLeft: {
    flex: 1,
    flexDirection: 'column',
  },
  headerIdBarRow: {
    flexDirection: 'row',
    borderBottom: '1pt solid #000',
  },
  headerIdBar: {
    flex: 1,
    backgroundColor: '#000',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '4 8',
  },
  headerIdBarLabel: {
    color: '#fff',
    fontSize: 7,
    fontFamily: 'Helvetica-Bold',
    letterSpacing: 1,
  },
  headerIdBarValue: {
    color: '#fff',
    fontSize: 10,
    fontFamily: 'Helvetica-Bold',
  },
  headerMetaRow: {
    flexDirection: 'row',
    borderBottom: '1pt solid #000',
  },
  headerMetaRowLast: {
    flexDirection: 'row',
  },
  metaCell: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: '4 6',
    borderRight: '1pt solid #000',
  },
  metaCellLast: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: '4 6',
  },
  metaLabel: {
    fontFamily: 'Helvetica-Bold',
    marginRight: 4,
  },
  metaCol: { width: '20%' },
  colContato: { width: '32%' },
  colEmail: { width: '32%' },
  colTelefone: { width: '18%' },
  colLocal: { width: '18%' },
  colTipoCapeamento: { width: '17%' },
  colLacre: { width: '18%' },

  table: {
    marginTop: 8,
  },
  tableTitle: {
    fontSize: 9,
    fontFamily: 'Helvetica-Bold',
    marginBottom: 4,
  },
  tableRow: {
    flexDirection: 'row',
    borderLeft: '1pt solid #000',
    borderRight: '1pt solid #000',
    borderTop: '1pt solid #000',
    borderBottom: '1pt solid #000',
  },
  tableHeaderRow: {
    borderTop: '1pt solid #000',
  },
  tableRowEven: {
    backgroundColor: '#f5f5f5',
  },
  th: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 6.5,
    padding: '4 4',
    borderRight: '1pt solid #000',
    textAlign: 'center',
  },
  thLast: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 6.5,
    padding: '4 4',
    textAlign: 'center',
  },
  td: {
    fontSize: 7,
    padding: '4 4',
    borderRight: '1pt solid #000',
    textAlign: 'center',
  },
  tdLast: {
    fontSize: 7,
    padding: '4 4',
    textAlign: 'center',
  },
  tdFcj: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 7.5,
  },
  colEtiqueta: { width: '18%' },
  colIdade: { width: '13%' },
  colDataRuptura: { width: '18%' },
  colFcjResultado: { width: '18%' },
  colTipoRupturaResultado: { width: '18%' },
  colPrensa: { flex: 1 },

  chartArea: {
    flex: 1,
    justifyContent: 'center',
  },
  criterioBox: {
    marginTop: 6,
    padding: 5,
    border: '0.75pt solid #ccc',
    backgroundColor: '#fafafa',
  },
  criterioText: {
    fontSize: 6.5,
    color: '#444',
    lineHeight: 1.35,
  },

  signatureRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 40,
  },
  signaturePlaceholder: {
    width: 220,
    height: 36,
    border: '0.75pt dashed #999',
    alignItems: 'center',
    justifyContent: 'center',
  },
  placeholderText: {
    fontSize: 5,
    color: '#888',
    textAlign: 'center',
  },
  footer: {
    position: 'absolute',
    bottom: 28.35,
    left: 28.35,
    right: 28.35,
    flexDirection: 'column',
    gap: 6,
  },
  footerPageRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
  },
  footerText: {
    fontSize: 7,
    color: '#666',
  },
});

// Exibe data (dd/mm/aaaa) no PDF.
function formatDateBrPdf(value) {
  if (!value) return '-';
  const s = String(value);
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) return s;
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    const [y, m, d] = s.slice(0, 10).split('-');
    return `${d}/${m}/${y}`;
  }
  const p = new Date(s);
  if (Number.isNaN(p.getTime())) return s;
  return p.toLocaleDateString('pt-BR');
}

// Exibe hora (HH:mm) no PDF, ou '-' se ausente.
function formatHoraPdf(value) {
  if (!value) return '-';
  const p = new Date(value);
  if (Number.isNaN(p.getTime())) return '-';
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(p.getHours())}:${pad(p.getMinutes())}`;
}

// Exibe um número com 2 casas decimais no PDF, ou '-' se ausente/inválido.
function formatNumeroPdf(value) {
  if (value == null || !Number.isFinite(Number(value))) return '-';
  return Number(value).toFixed(2);
}

const TEXTO_CRITERIO =
  'Critério de Aceitação (NBR 12655): amostra com 100% dos ensaios de 28 dias com resistência ' +
  'igual ou superior ao Fck especificado para a estrutura.';

// Cabeçalho de um caminhão: `fixed` para repetir em toda página física gerada a partir do seu
// <Page> lógico (inclusive continuação automática por overflow de tabelas grandes).
function HeaderCaminhao({ caminhao, cabecalhoBase, totalCps, logoCliente = null }) {
  return (
    <View style={styles.headerBox} fixed>
      <View style={styles.headerTop}>
        <View style={styles.headerTitleBlock}>
          <Text style={styles.headerTitle}>RELATÓRIO POR CAMINHÃO — RESULTADOS DE RUPTURA</Text>
          <Text style={styles.headerSubtitle}>NBR 5738:2015, 5739:2018 E 12655:2022</Text>
        </View>

        <View style={styles.headerBrandBlock}>
          <LogoRelatorio src={logoCliente} width={100} height={60} />
          <View>
            <Text style={styles.headerEmpresaLabel}>Endereço</Text>
            <Text style={styles.headerEmpresaValue}>{cabecalhoBase.endereco || '-'}</Text>
          </View>
        </View>
      </View>

      <View style={styles.headerLower}>
        <View style={styles.headerLowerLeft}>
          <View style={styles.headerIdBarRow}>
            <View style={styles.headerIdBar}>
              <Text style={styles.headerIdBarLabel}>PLACA DO CAMINHÃO</Text>
              <Text style={styles.headerIdBarValue}>{caminhao.placaCaminhao || '-'}</Text>
            </View>
            <View style={styles.headerBrandFill} />
          </View>

          <View style={styles.headerMetaRow}>
            <View style={[styles.metaCell, styles.metaCol]}>
              <Text style={styles.metaLabel}>Empresa</Text>
              <Text>{cabecalhoBase.cliente || '-'}</Text>
            </View>
            <View style={[styles.metaCell, styles.metaCol]}>
              <Text style={styles.metaLabel}>Cliente</Text>
              <Text>{cabecalhoBase.cliente || '-'}</Text>
            </View>
            <View style={[styles.metaCell, styles.metaCol]}>
              <Text style={styles.metaLabel}>Obra</Text>
              <Text>{cabecalhoBase.obra || '-'}</Text>
            </View>
            <View style={[styles.metaCell, styles.metaCol]}>
              <Text style={styles.metaLabel}>CNPJ</Text>
              <Text>{cabecalhoBase.cnpj || '-'}</Text>
            </View>
            <View style={[styles.metaCellLast, styles.metaCol]}>
              <Text style={styles.metaLabel}>Nº CPs</Text>
              <Text>{totalCps}</Text>
            </View>
          </View>

          <View style={styles.headerMetaRow}>
            <View style={[styles.metaCell, styles.metaCol]}>
              <Text style={styles.metaLabel}>Cimento</Text>
              <Text>{cabecalhoBase.cimento || '-'}</Text>
            </View>
            <View style={[styles.metaCell, styles.metaCol]}>
              <Text style={styles.metaLabel}>Fck</Text>
              <Text>{cabecalhoBase.fck || '-'}</Text>
            </View>
            <View style={[styles.metaCell, styles.metaCol]}>
              <Text style={styles.metaLabel}>Tipo de Concreto</Text>
              <Text>{cabecalhoBase.tipoConcreto || '-'}</Text>
            </View>
            <View style={[styles.metaCell, styles.metaCol]}>
              <Text style={styles.metaLabel}>Tipo de CP</Text>
              <Text>{cabecalhoBase.tipoCorpoProva || '-'}</Text>
            </View>
            <View style={[styles.metaCellLast, styles.metaCol]}>
              <Text style={styles.metaLabel}>Concreteira</Text>
              <Text>{cabecalhoBase.concreteira || '-'}</Text>
            </View>
          </View>

          <View style={styles.headerMetaRowLast}>
            <View style={[styles.metaCell, styles.metaCol]}>
              <Text style={styles.metaLabel}>Lacre</Text>
              <Text>{caminhao.lacre || '-'}</Text>
            </View>
            <View style={[styles.metaCell, styles.metaCol]}>
              <Text style={styles.metaLabel}>Volume (m³)</Text>
              <Text>{caminhao.valrVolume ?? '-'}</Text>
            </View>
            <View style={[styles.metaCell, styles.metaCol]}>
              <Text style={styles.metaLabel}>Saída Usina</Text>
              <Text>{formatHoraPdf(caminhao.dataSaidaUsina)}</Text>
            </View>
            <View style={[styles.metaCell, styles.metaCol]}>
              <Text style={styles.metaLabel}>Início Descarga</Text>
              <Text>{formatHoraPdf(caminhao.dataInicioDescarga)}</Text>
            </View>
            <View style={[styles.metaCellLast, styles.metaCol]}>
              <Text style={styles.metaLabel}>Fim Descarga</Text>
              <Text>{formatHoraPdf(caminhao.dataFimDescarga)}</Text>
            </View>
          </View>
        </View>

        <View style={styles.headerQrColumn}>
          <View style={styles.metaQrPlaceholder}>
            <Text style={styles.placeholderText}>QR</Text>
          </View>
          <Text style={styles.metaQrCaption}>Armazenamento Digital do{'\n'}Cliente/Obra em Núvem</Text>
        </View>
      </View>
    </View>
  );
}

// Rodapé de assinaturas + numeração de página: fixed para repetir em todas as páginas.
function FooterAssinaturas({ assinaturas = [] }) {
  return (
    <View style={styles.footer} fixed>
      <AssinaturasRodape assinaturas={assinaturas} />
      <View style={styles.footerPageRow}>
        <Text
          style={styles.footerText}
          render={({ pageNumber, totalPages }) => `${pageNumber}/${totalPages}`}
        />
      </View>
    </View>
  );
}

// Documento PDF (@react-pdf) do "Relatório por caminhão": para cada caminhão, uma página com a
// tabela resumida de rupturas, seguida (após todas as páginas de tabela) por uma página com a
// curva de evolução da resistência (MPa x dias).
function RelatorioCaminhaoPDF({
  grupos = [],
  cabecalhoBase = {},
  corAtiva = true,
  logoCliente = null,
  assinaturas = [],
}) {
  return (
    <Document>
      {/* Capa */}
      <Page size="A4" style={styles.coverPage}>
        <View style={styles.coverBrandRow}>
          <Image style={styles.coverIcon} src={logoIcon} />
          <View style={styles.coverBrandText}>
            <Text style={styles.coverWordmark}>concreto.tec.br</Text>
            <Text style={styles.coverTagline}>TECNOLOGIA EM CONCRETO</Text>
          </View>
        </View>

        <View style={styles.coverSpacer}>
          <View style={styles.coverTitleBlock}>
            <Text style={styles.coverKicker}>RELATÓRIO POR CAMINHÃO</Text>
            <Text style={styles.coverTitle}>Resultados de Ruptura de Corpos de Prova</Text>
            <Text style={styles.coverSubtitle}>NBR 5738:2015, 5739:2018 E 12655:2022</Text>

            <View style={styles.coverMetaGrid}>
              <View style={styles.coverMetaField}>
                <Text style={styles.coverMetaLabel}>CLIENTE</Text>
                <Text style={styles.coverMetaValue}>{cabecalhoBase.cliente || '-'}</Text>
              </View>
              <View style={styles.coverMetaField}>
                <Text style={styles.coverMetaLabel}>OBRA</Text>
                <Text style={styles.coverMetaValue}>{cabecalhoBase.obra || '-'}</Text>
              </View>
              <View style={styles.coverMetaField}>
                <Text style={styles.coverMetaLabel}>DATA DE EMISSÃO</Text>
                <Text style={styles.coverMetaValue}>{formatDateBrPdf(new Date())}</Text>
              </View>
            </View>
          </View>
        </View>

        <View style={styles.coverFooter}>
          <Text>concreto.tec.br</Text>
          <Text>Documento gerado eletronicamente</Text>
        </View>
      </Page>

      {/* Por caminhão: página(s) de tabela seguidas imediatamente da página de gráfico daquele
          mesmo caminhão — o gráfico só vem depois de todas as páginas de dados de ruptura que
          pertencem a ele, antes de passar ao próximo caminhão. */}
      {grupos.map(({ caminhao, linhas, fck, ponderadorTipoCimento }) => {
        const pontosMedidos = linhas
          .filter((item) => item.temResultado)
          .map((item) => ({ idade: Number(item.idadeRuptura), fcj: item.fcj }));

        return (
          <React.Fragment key={caminhao.id}>
            <Page size="A4" orientation="landscape" style={styles.page}>
              <HeaderCaminhao caminhao={caminhao} cabecalhoBase={cabecalhoBase} totalCps={linhas.length} logoCliente={logoCliente} />

              {/* Tabela de resultados de ruptura */}
              <View style={styles.table}>
                <Text style={styles.tableTitle}>Resultados das Rupturas de Corpos de Prova</Text>

                <View style={[styles.tableRow, styles.tableHeaderRow]} wrap={false}>
                  <Text style={[styles.th, styles.colEtiqueta]}>Identificador (CP)</Text>
                  <Text style={[styles.th, styles.colIdade]}>Idade (dias)</Text>
                  <Text style={[styles.th, styles.colDataRuptura]}>Data Ruptura</Text>
                  <Text style={[styles.th, styles.colFcjResultado]}>Resistência Fcj (MPa)</Text>
                  <Text style={[styles.th, styles.colTipoRupturaResultado]}>Tipo de Ruptura</Text>
                  <Text style={[styles.thLast, styles.colPrensa]}>Prensa</Text>
                </View>

                {linhas.map((item, index) => {
                  const isEven = index % 2 === 1;
                  return (
                    <View key={item.id} style={[styles.tableRow, isEven ? styles.tableRowEven : null]} wrap={false}>
                      <Text style={[styles.td, styles.colEtiqueta]}>{item.etiqueta || '-'}</Text>
                      <Text style={[styles.td, styles.colIdade]}>{item.idadeRuptura ?? '-'}</Text>
                      <Text style={[styles.td, styles.colDataRuptura]}>{formatDateBrPdf(item.dataRuptura)}</Text>
                      <Text
                        style={[
                          styles.td,
                          styles.colFcjResultado,
                          styles.tdFcj,
                          corAtiva && item.corFcj
                            ? { backgroundColor: `${CORES_FCJ[item.corFcj]}73`, color: CORES_FCJ_TEXTO[item.corFcj] }
                            : null,
                        ]}
                      >
                        {item.temResultado ? formatNumeroPdf(item.fcj) : 'Aguardando'}
                      </Text>
                      <Text style={[styles.td, styles.colTipoRupturaResultado]}>{item.tipoRuptura || '-'}</Text>
                      <Text style={[styles.tdLast, styles.colPrensa]}>{item.prensa || '-'}</Text>
                    </View>
                  );
                })}
              </View>

              {/* Critério de aceitação */}
              <View style={styles.criterioBox} wrap={false}>
                <Text style={styles.criterioText}>{TEXTO_CRITERIO}</Text>
              </View>

              <FooterAssinaturas assinaturas={assinaturas} />
            </Page>

            <Page size="A4" orientation="landscape" style={styles.page}>
              <HeaderCaminhao caminhao={caminhao} cabecalhoBase={cabecalhoBase} totalCps={linhas.length} logoCliente={logoCliente} />

              {/* Gráfico de evolução da resistência, ocupando o restante da página */}
              <View style={styles.chartArea}>
                <CurvaResistenciaChart
                  pontosMedidos={pontosMedidos}
                  fck={fck}
                  ponderadorTipoCimento={ponderadorTipoCimento}
                  width={760}
                  height={260}
                />
              </View>

              <FooterAssinaturas assinaturas={assinaturas} />
            </Page>
          </React.Fragment>
        );
      })}
    </Document>
  );
}

export default RelatorioCaminhaoPDF;
